/**
 * Celebration Registry — Single Source of Truth
 * 
 * ALL celebration types, subtypes, emojis, labels, and display metadata
 * are defined here. Every other file in the codebase imports from this registry.
 * 
 * DO NOT hardcode celebration types elsewhere.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CelebrationSubtypeEntry {
  key: string;
  label: string;
}

export interface CelebrationTypeEntry {
  key: string;
  label: string;
  emoji: string;
  tier: 1 | 2 | 3;
  /** Optional subtypes — only for types where structure fundamentally changes */
  subtypes?: CelebrationSubtypeEntry[];
  /** Landing page tagline */
  tagline: string;
  /** Path to SVG illustration (relative to /assets/) */
  svgPath: string;
  /** Tailwind gradient classes for landing page card */
  gradient: string;
  /** If true, this type is internal-only and hidden from UI (e.g. 'personal') */
  internal?: boolean;
}

export interface RegionalTypeOrder {
  region: string;
  /** Ordered list of type keys — types listed first appear first */
  typeOrder: string[];
}

// ---------------------------------------------------------------------------
// The Registry
// ---------------------------------------------------------------------------

export const CELEBRATION_TYPES: CelebrationTypeEntry[] = [
  // ── Tier 1: Universal ──────────────────────────────────────────────────
  {
    key: 'wedding',
    label: 'Wedding',
    emoji: '💒',
    tier: 1,
    tagline: 'Your dream day, perfectly orchestrated',
    svgPath: '/assets/wedding.svg',
    gradient: 'from-celebration-pink/20 to-celebration-purple/10',
    subtypes: [
      { key: 'religious', label: 'Religious Ceremony' },
      { key: 'civil', label: 'Civil / Legal Ceremony' },
      { key: 'destination', label: 'Destination Wedding' },
      { key: 'elopement', label: 'Elopement' },
      { key: 'vow_renewal', label: 'Vow Renewal' },
    ],
  },
  {
    key: 'birthday',
    label: 'Birthday',
    emoji: '🎂',
    tier: 1,
    tagline: 'From intimate dinners to blowout bashes',
    svgPath: '/assets/birthday.svg',
    gradient: 'from-celebration-orange/20 to-celebration-yellow/10',
    subtypes: [
      { key: 'kids', label: 'Kids (1–12)' },
      { key: 'teen', label: 'Teen (13–17)' },
      { key: 'sweet_16', label: 'Sweet 16' },
      { key: 'milestone_18_21', label: 'Milestone 18 / 21' },
      { key: 'milestone_30', label: '30th Birthday' },
      { key: 'milestone_40', label: '40th Birthday' },
      { key: 'milestone_50', label: '50th Birthday' },
      { key: 'milestone_60_plus', label: '60th+ Birthday' },
      { key: 'milestone', label: 'Milestone (other)' },
      { key: 'adult', label: 'Standard Adult' },
    ],
  },
  {
    key: 'anniversary',
    label: 'Anniversary',
    emoji: '💕',
    tier: 1,
    tagline: 'Celebrate milestones that matter',
    svgPath: '/assets/anniversary.svg',
    gradient: 'from-celebration-purple/20 to-celebration-blue/10',
  },
  {
    key: 'engagement',
    label: 'Engagement',
    emoji: '💍',
    tier: 1,
    tagline: 'The beginning of forever',
    svgPath: '/assets/engagement.svg',
    gradient: 'from-celebration-pink/20 to-celebration-orange/10',
  },
  {
    key: 'baby_shower',
    label: 'Baby Shower',
    emoji: '🍼',
    tier: 1,
    tagline: 'Welcome the newest arrival in style',
    svgPath: '/assets/baby_shower.svg',
    gradient: 'from-celebration-blue/20 to-celebration-green/10',
  },
  {
    key: 'graduation',
    label: 'Graduation',
    emoji: '🎓',
    tier: 1,
    tagline: 'Honor achievements with style',
    svgPath: '/assets/graduation.svg',
    gradient: 'from-celebration-green/20 to-celebration-yellow/10',
  },

  // ── Tier 2: Culturally significant ─────────────────────────────────────
  {
    key: 'religious_ceremony',
    label: 'Religious Ceremony',
    emoji: '🙏',
    tier: 2,
    tagline: 'Sacred traditions, beautifully planned',
    svgPath: '/assets/religious_ceremony.svg',
    gradient: 'from-celebration-yellow/20 to-celebration-orange/10',
  },
  {
    key: 'coming_of_age',
    label: 'Coming of Age',
    emoji: '🌟',
    tier: 2,
    tagline: 'Mark the transition to adulthood',
    svgPath: '/assets/coming_of_age.svg',
    gradient: 'from-celebration-purple/20 to-celebration-pink/10',
    subtypes: [
      { key: 'quinceanera', label: 'Quinceañera' },
      { key: 'bar_bat_mitzvah', label: 'Bar / Bat Mitzvah' },
      { key: 'sweet_16', label: 'Sweet 16' },
      { key: 'debut', label: 'Debut (Filipino)' },
      { key: 'traditional_ceremony', label: 'Traditional Ceremony' },
    ],
  },
  {
    key: 'memorial',
    label: 'Memorial / Celebration of Life',
    emoji: '🕊️',
    tier: 2,
    tagline: 'Honor and remember loved ones',
    svgPath: '/assets/memorial.svg',
    gradient: 'from-slate-200/40 to-slate-100/20',
    subtypes: [
      { key: 'funeral', label: 'Funeral Service' },
      { key: 'celebration_of_life', label: 'Celebration of Life' },
      { key: 'wake', label: 'Wake / Visitation' },
    ],
  },
  {
    key: 'naming_ceremony',
    label: 'Naming Ceremony',
    emoji: '👶',
    tier: 2,
    tagline: 'Celebrate new beginnings',
    svgPath: '/assets/naming_ceremony.svg',
    gradient: 'from-celebration-blue/20 to-celebration-purple/10',
  },
  {
    key: 'housewarming',
    label: 'Housewarming',
    emoji: '🏠',
    tier: 2,
    tagline: 'Make your new house a home',
    svgPath: '/assets/housewarming.svg',
    gradient: 'from-celebration-orange/20 to-celebration-green/10',
  },
  {
    key: 'retirement',
    label: 'Retirement',
    emoji: '🎊',
    tier: 2,
    tagline: 'Cheers to a new chapter',
    svgPath: '/assets/retirement.svg',
    gradient: 'from-celebration-yellow/20 to-celebration-blue/10',
  },

  // ── Tier 3: Lower frequency ────────────────────────────────────────────
  {
    key: 'reunion',
    label: 'Reunion',
    emoji: '👨‍👩‍👧‍👦',
    tier: 3,
    tagline: 'Bring everyone back together',
    svgPath: '/assets/reunion.svg',
    gradient: 'from-celebration-green/20 to-celebration-blue/10',
    subtypes: [
      { key: 'family', label: 'Family Reunion' },
      { key: 'high_school', label: 'High School Reunion' },
      { key: 'college', label: 'College / Alumni Reunion' },
      { key: 'workplace', label: 'Workplace / Team Reunion' },
      { key: 'friends', label: 'Friends Reunion' },
      { key: 'hometown', label: 'Hometown / Village Reunion' },
      { key: 'class', label: 'Class Reunion' },
      { key: 'military', label: 'Military Reunion' },
      { key: 'community', label: 'Community Reunion' },
    ],
  },
  {
    key: 'fundraiser',
    label: 'Fundraiser / Charity',
    emoji: '🎗️',
    tier: 3,
    tagline: 'Events that make a difference',
    svgPath: '/assets/fundraiser.svg',
    gradient: 'from-celebration-pink/20 to-celebration-green/10',
    subtypes: [
      { key: 'gala', label: 'Charity Gala' },
      { key: 'charity_run', label: 'Charity Run / Walk' },
      { key: 'auction', label: 'Auction' },
      { key: 'benefit_concert', label: 'Benefit Concert' },
    ],
  },
  {
    key: 'festival',
    label: 'Festival / Cultural Event',
    emoji: '🪔',
    tier: 3,
    tagline: 'Celebrate culture and community',
    svgPath: '/assets/festival.svg',
    gradient: 'from-celebration-orange/20 to-celebration-purple/10',
    subtypes: [
      { key: 'religious_festival', label: 'Religious Festival' },
      { key: 'cultural_festival', label: 'Cultural Festival' },
      { key: 'seasonal', label: 'Seasonal Celebration' },
      { key: 'community', label: 'Community Festival' },
    ],
  },
  {
    key: 'other',
    label: 'Other',
    emoji: '✨',
    tier: 3,
    tagline: 'Any celebration you can dream up',
    svgPath: '/assets/other_celebration.svg',
    gradient: 'from-celebration-blue/20 to-celebration-yellow/10',
  },

  // ── Internal (hidden from UI) ──────────────────────────────────────────
  {
    key: 'personal',
    label: 'Personal Events',
    emoji: '📋',
    tier: 3,
    tagline: '',
    svgPath: '',
    gradient: '',
    internal: true,
  },
];

// ---------------------------------------------------------------------------
// Regional type ordering
// ---------------------------------------------------------------------------

export const REGIONAL_TYPE_ORDERS: RegionalTypeOrder[] = [
  {
    region: 'IN',
    typeOrder: ['wedding', 'engagement', 'religious_ceremony', 'birthday', 'naming_ceremony', 'coming_of_age', 'festival', 'anniversary', 'baby_shower', 'housewarming', 'retirement', 'graduation', 'memorial', 'reunion', 'fundraiser', 'other'],
  },
  {
    region: 'US',
    typeOrder: ['wedding', 'birthday', 'graduation', 'baby_shower', 'engagement', 'anniversary', 'coming_of_age', 'memorial', 'religious_ceremony', 'retirement', 'housewarming', 'reunion', 'fundraiser', 'festival', 'naming_ceremony', 'other'],
  },
  {
    region: 'MX',
    typeOrder: ['wedding', 'coming_of_age', 'birthday', 'religious_ceremony', 'anniversary', 'engagement', 'baby_shower', 'graduation', 'naming_ceremony', 'festival', 'memorial', 'housewarming', 'retirement', 'reunion', 'fundraiser', 'other'],
  },
  {
    region: 'IL',
    typeOrder: ['wedding', 'coming_of_age', 'birthday', 'memorial', 'anniversary', 'religious_ceremony', 'engagement', 'baby_shower', 'graduation', 'festival', 'retirement', 'housewarming', 'reunion', 'fundraiser', 'naming_ceremony', 'other'],
  },
  {
    region: 'AE',
    typeOrder: ['wedding', 'engagement', 'festival', 'religious_ceremony', 'birthday', 'naming_ceremony', 'anniversary', 'baby_shower', 'coming_of_age', 'graduation', 'memorial', 'housewarming', 'retirement', 'reunion', 'fundraiser', 'other'],
  },
  {
    region: 'CN',
    typeOrder: ['wedding', 'birthday', 'festival', 'anniversary', 'baby_shower', 'naming_ceremony', 'engagement', 'graduation', 'coming_of_age', 'housewarming', 'retirement', 'memorial', 'reunion', 'fundraiser', 'religious_ceremony', 'other'],
  },
  {
    region: 'GB',
    typeOrder: ['wedding', 'birthday', 'anniversary', 'engagement', 'baby_shower', 'graduation', 'coming_of_age', 'memorial', 'religious_ceremony', 'retirement', 'housewarming', 'festival', 'reunion', 'fundraiser', 'naming_ceremony', 'other'],
  },
  {
    region: 'NG',
    typeOrder: ['wedding', 'naming_ceremony', 'birthday', 'religious_ceremony', 'coming_of_age', 'festival', 'engagement', 'anniversary', 'graduation', 'baby_shower', 'memorial', 'housewarming', 'retirement', 'reunion', 'fundraiser', 'other'],
  },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** All type keys exposed to users (excludes internal types like 'personal') */
export function getPublicTypes(): CelebrationTypeEntry[] {
  return CELEBRATION_TYPES.filter(t => !t.internal);
}

/** All type keys (including internal — for backend validation) */
export function getAllTypeKeys(): string[] {
  return CELEBRATION_TYPES.map(t => t.key);
}

/** Public type keys only */
export function getPublicTypeKeys(): string[] {
  return getPublicTypes().map(t => t.key);
}

/** Check if a string is a valid celebration type (including internal) */
export function isValidCelebrationType(value: string): boolean {
  return CELEBRATION_TYPES.some(t => t.key === value);
}

/** Check if a string is a valid public celebration type */
export function isValidPublicCelebrationType(value: string): boolean {
  return getPublicTypes().some(t => t.key === value);
}

/** Get a type entry by key */
export function getTypeByKey(key: string): CelebrationTypeEntry | undefined {
  return CELEBRATION_TYPES.find(t => t.key === key);
}

/** Get emoji for a given celebration type key */
export function getEmojiForType(key: string): string {
  return getTypeByKey(key)?.emoji ?? '🎉';
}

/** Get label for a given celebration type key */
export function getLabelForType(key: string): string {
  return getTypeByKey(key)?.label ?? key;
}

/** Get subtypes for a given celebration type key, or empty array */
export function getSubtypesForType(key: string): CelebrationSubtypeEntry[] {
  return getTypeByKey(key)?.subtypes ?? [];
}

/** Check if a subtype is valid for a given type */
export function isValidSubtype(typeKey: string, subtypeKey: string | null | undefined): boolean {
  if (!subtypeKey) return true; // null/undefined subtype is always valid
  const subtypes = getSubtypesForType(typeKey);
  if (subtypes.length === 0) return subtypeKey === null || subtypeKey === undefined;
  return subtypes.some(s => s.key === subtypeKey);
}

/** Get label for a subtype */
export function getSubtypeLabel(typeKey: string, subtypeKey: string): string {
  const subtypes = getSubtypesForType(typeKey);
  return subtypes.find(s => s.key === subtypeKey)?.label ?? subtypeKey;
}

/** Build emoji map: { wedding: '💒', birthday: '🎂', ... } */
export function buildEmojiMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const t of CELEBRATION_TYPES) {
    map[t.key] = t.emoji;
  }
  return map;
}

/** Build form options: [{ value: 'wedding', label: 'Wedding', emoji: '💒' }, ...] */
export function buildFormOptions(): Array<{ value: string; label: string; emoji: string }> {
  return getPublicTypes().map(t => ({
    value: t.key,
    label: t.label,
    emoji: t.emoji,
  }));
}

/**
 * Get types ordered by region (detected from browser locale).
 * Falls back to US ordering for unknown regions.
 */
export function getOrderedTypes(locale?: string): CelebrationTypeEntry[] {
  const regionCode = extractRegionFromLocale(locale);
  const regional = REGIONAL_TYPE_ORDERS.find(r => r.region === regionCode);
  const publicTypes = getPublicTypes();

  if (!regional) return publicTypes;

  const ordered: CelebrationTypeEntry[] = [];
  for (const key of regional.typeOrder) {
    const entry = publicTypes.find(t => t.key === key);
    if (entry) ordered.push(entry);
  }
  // Append any types not in the regional order (safety net)
  for (const entry of publicTypes) {
    if (!ordered.includes(entry)) ordered.push(entry);
  }
  return ordered;
}

/**
 * Get ordered form options based on locale
 */
export function getOrderedFormOptions(locale?: string): Array<{ value: string; label: string; emoji: string }> {
  return getOrderedTypes(locale).map(t => ({
    value: t.key,
    label: t.label,
    emoji: t.emoji,
  }));
}

/**
 * Extract ISO 3166-1 alpha-2 region code from a BCP 47 locale string.
 * e.g. 'en-US' → 'US', 'hi-IN' → 'IN', 'en' → undefined
 */
function extractRegionFromLocale(locale?: string): string | undefined {
  if (!locale) {
    if (typeof navigator !== 'undefined') {
      locale = navigator.language;
    }
  }
  if (!locale) return undefined;
  const parts = locale.split('-');
  if (parts.length >= 2) {
    return parts[parts.length - 1].toUpperCase();
  }
  return undefined;
}
